/**
 * A rating, as five stars.
 *
 * "4.6" is a number you have to convert; five stars with four and a bit filled
 * is a quantity you read at a glance — which is the whole job on a card
 * someone is scanning. The number stays next to them, because the stars alone
 * lose the precision, and the review count stays too: a bare "4.5" is what
 * sends people to Google Maps to check whether it means anything (§7b).
 *
 * Drawn as one SVG with a clip, not five icons at three opacities. A partial
 * star has to be partially filled — rounding 4.6 to five whole stars is a
 * different claim from the one the data makes.
 */

interface Props {
  /** 0–5. */
  rating: number
  size?: number
}

const STAR =
  'M12 2.6l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.4l-5.8 3.06 1.1-6.46-4.69-4.58 6.49-.94z'

export function Stars({ rating, size = 14 }: Props) {
  const clamped = Math.max(0, Math.min(5, rating))
  // A unique id per instance: several cards share a screen, and a duplicated
  // clipPath id would make every rating render the first card's fill.
  const id = `stars-${Math.round(clamped * 100)}-${size}`

  return (
    <span className="stars" aria-hidden>
      <svg
        viewBox="0 0 120 24"
        width={size * 5}
        height={size}
        fill="none"
        role="presentation"
      >
        <defs>
          <clipPath id={id}>
            <rect x="0" y="0" width={(clamped / 5) * 120} height="24" />
          </clipPath>
        </defs>

        {/* The empty five, always drawn: they are the scale the fill is read
            against, so a 2-star place has to show what it is 2 out of. */}
        <g className="stars-empty">
          {[0, 1, 2, 3, 4].map((i) => (
            <path key={i} d={STAR} transform={`translate(${i * 24} 0)`} />
          ))}
        </g>

        {/* The same five, filled and clipped to the rating. */}
        <g className="stars-filled" clipPath={`url(#${id})`}>
          {[0, 1, 2, 3, 4].map((i) => (
            <path key={i} d={STAR} transform={`translate(${i * 24} 0)`} />
          ))}
        </g>
      </svg>
    </span>
  )
}
